export interface DesiredBounds {
  x?: number;
  y?: number;
  width: number;
  height: number;
}

export interface BoxSize {
  width: number;
  height: number;
}

export interface ResolvedBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(v, hi));

/**
 * 윈도우 초기 bounds 를 부모 가용 영역으로 clamp 한다.
 *
 * react-rnd `bounds='parent'` 는 자식이 부모보다 크면 드래그 여유를 0 으로 만들어 윈도우를
 * 잠근다(#78: "창 작을 때 각종 창 이동 안 됨"). standard/ceoConsole 처럼 minWidth 가 1000 인
 * 프리셋은 좁은 뷰포트에서 항상 부모를 넘쳐 잠겼다.
 *
 * - 부모가 유효(>0)하면 width/height 를 부모 크기로 clamp 하되, 프리셋 min 으로 바닥을 잡는다.
 *   단 뷰포트가 min 보다도 좁으면 effective min 을 부모 크기까지 낮춰 윈도우가 언제나 뷰포트
 *   안에 들어오게 한다(콘텐츠는 내부 스크롤).
 * - 위치 미지정이면 중앙 배치 후 [0, parent - size] 로 clamp 해 전체가 보이도록 한다.
 * - 부모가 0×0(detached/SSR/jsdom)이면 크기를 손대지 않고 기존 폴백 위치(60,40)를 유지한다.
 */
export function resolveInitialBounds(
  desired: DesiredBounds,
  parent: BoxSize,
  min: BoxSize
): ResolvedBounds {
  const hasParent = parent.width > 0 && parent.height > 0;
  if (!hasParent) {
    return {
      x: desired.x ?? 60,
      y: desired.y ?? 40,
      width: desired.width,
      height: desired.height,
      minWidth: min.width,
      minHeight: min.height,
    };
  }

  const minWidth = Math.min(min.width, parent.width);
  const minHeight = Math.min(min.height, parent.height);
  const width = clamp(desired.width, minWidth, parent.width);
  const height = clamp(desired.height, minHeight, parent.height);

  const centerX = Math.max(0, Math.round((parent.width - width) / 2));
  const centerY = Math.max(0, Math.round((parent.height - height) / 2));
  const x = clamp(desired.x ?? centerX, 0, parent.width - width);
  const y = clamp(desired.y ?? centerY, 0, parent.height - height);

  return { x, y, width, height, minWidth, minHeight };
}
